import TeamActions from 'common/redux/actions/teamMaster';
import { entityMasterSingleEditFactory } from 'common/entity/generic/SingleEntityEditContainer';
import { MasterTeam } from 'common/models/internal/MasterTeam';
import {
    teamMaster as teamApi,
    clientCities as clientCitiesApi
} from 'common/middleware/api';
import { FieldTypeEnum } from 'common/enums/FieldTypeEnum';
import BusinessUnitIdInput from 'common/entity/teamMaster/BusinessUnitIdInput';
import CodeInputWithId from 'common/entity/generic/CodeInputWithId';
import { BusinessUnitId } from 'common/models/internal/BusinessUnits';
import { IncludeTypeEnums } from 'common/enums/EntityMasterEnum';
import { ENTITY_NAME_ID } from 'common/constants/entity/teamMaster';
import { isGlobalPersonnelSelector } from 'common/redux/selectors/sessionSelector';
import { CityId } from 'common/models/internal/CityId';
import teamMasterIDs from 'common/tests/masterManagement/teamMaster.json';

const TEAM_FIELDS = [
    {
        accessor: 'code',
        label: 'teamMasterForm.code',
        testId: teamMasterIDs.addNewTeam.inputCode,
        helpText: 'teamMasterForm.codeTooltip',
        placeholder: 'teamMasterForm.codePlaceholder',
        halfWidth: false,
        disabledOnEdit: true,
        required: true,
        type: FieldTypeEnum.Text,
        component: CodeInputWithId,
        extraProps: ({ entity, field, props }) => {
            const {
                validateDuplicateId,
                onValidateDuplicateIdChange,
                clientId
            } = props;
            return {
                validateDuplicateId,
                onValidateDuplicateIdChange,
                id: entity.get('id'),
                code: entity.getIn(field.absoluteAccessor),
                clientId
            };
        },
        onChangeHandler: ({ entity, value }) => {
            const { id, code } = value;
            if ('id' in value) {
                return entity.setId(id);
            }
            if ('code' in value) {
                return entity.setCode(code);
            }
        }
    },
    {
        accessor: 'name',
        label: 'teamMasterForm.name',
        testId: teamMasterIDs.addNewTeam.inputName,
        placeholder: 'teamMasterForm.namePlaceholder',
        required: true,
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'color',
        label: 'teamMasterForm.color',
        testId: teamMasterIDs.addNewTeam.inputColor,
        placeholder: 'teamMasterForm.colorPlaceholder',
        type: FieldTypeEnum.Text,
        // NOTE: only exposing to global personnel & in edit mode
        isHidden: props => {
            return !(props.isEditing && props.specificProps.isGlobalPersonnel);
        }
    },
    {
        accessor: 'businessUnitId',
        label: 'teamMasterForm.businessUnitId',
        testId: teamMasterIDs.addNewTeam.inputBusinessUnitId,
        placeholder: 'teamMasterForm.businessUnitIdPlaceholder',
        component: BusinessUnitIdInput,
        extraProps: ({ entity, field, t }) => {
            return {
                popOverContent: field.validationMessage,
                isPopOverOpen: !!field.validationMessage,
                businessUnit: entity.get('businessUnit'),
                t
            };
        },
        onChangeHandler: ({ entity, value: businessUnit }) => {
            let businessUnitId = null;
            if (businessUnit) {
                businessUnitId = BusinessUnitId.fromJS({
                    clientId: entity.clientId,
                    businessUnitId: businessUnit.businessUnitId
                });
            }
            return entity
                .setBusinessUnit(businessUnit)
                .setBusinessUnitId(businessUnitId);
        }
    },
    {
        accessor: 'cityId',
        label: 'teamMasterForm.cityId',
        testId: teamMasterIDs.addNewTeam.inputCityId,
        placeholder: 'teamMasterForm.cityIdPlaceholder',
        type: FieldTypeEnum.Select,
        getProcessedValueForField: ({ entity }) => entity.cityId?.cityId,
        options: ({ field }) => {
            const { cities } = field.fetchInfo.response;
            return (
                cities &&
                cities.map(({ cityId, name }) => ({
                    value: cityId,
                    label: name
                }))
            );
        },
        fetchInfo: {
            fetcher: clientCitiesApi.fetch.bind(clientCitiesApi),
            params: ({ props }) => {
                const {
                    clientId,
                    localeSettings: { countryCode }
                } = props;
                return {
                    clientId,
                    countryId: countryCode
                };
            }
        },
        onChangeHandler: ({ entity, value }) => {
            let cityId = null;
            if (value) {
                cityId = CityId.fromJS({
                    cityId: value.value
                });
            }
            return entity.setCityId(cityId);
        },
        extraProps: () => ({
            isSearchable: true
        })
    },
    {
        accessor: 'customProperties',
        type: FieldTypeEnum.CustomFields,
        extraProps: () => {
            return {
                settingsName: 'teamSettings'
            };
        }
    }
];

const TEAM_INITIAL_FETCH_FIELDS = ['cityId'];

const SingleTeamEditContainer = entityMasterSingleEditFactory({
    MasterEntity: MasterTeam,
    MasterActions: TeamActions,
    extraApiParams: {
        include: [IncludeTypeEnums.BusinessUnit]
    },
    EntityFetchAPI: teamApi,
    INITIAL_FETCH_FIELDS: TEAM_INITIAL_FETCH_FIELDS,
    FIELDS: TEAM_FIELDS,
    options: {
        ENTITY_NAME_ID,
        PARAM_ID: 'teamId',
        ENTITY_SETTINGS_NAME: 'teamSettings'
    },
    mapEntityStateToProps: ({ state, props }) => ({
        isGlobalPersonnel: isGlobalPersonnelSelector(state, props)
    })
});

export default SingleTeamEditContainer;
